import { getAuthErrorMessage } from '@/lib/locale';
import { m } from '@/locale/paraglide/messages';
import { AuthCard } from '@/components/auth/auth-card';
import { FormError } from '@/components/shared/form-error';
import { FormSuccess } from '@/components/shared/form-success';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { websiteConfig } from '@/config/website';
import { authClient } from '@/auth/client';
import { Routes } from '@/lib/routes';
import { getFreeImageTrialHref } from '@/lib/free-image-trial';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconEye, IconEyeOff, IconLoader2 } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { DividerWithText } from './divider-with-text';
import type { LoginCreditOfferContext } from './login-credit-offer';
import { LoginCreditOfferPanel } from './login-credit-offer-panel';
import { SocialLoginButton } from './social-login-button';
interface RegisterFormProps {
  callbackUrl?: string;
  creditOffer?: LoginCreditOfferContext;
  showBottomLink?: boolean;
}
export function RegisterForm({
  callbackUrl: propCallbackUrl,
  creditOffer,
  showBottomLink = true,
}: RegisterFormProps) {
  const paramCallbackUrl =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('callbackUrl')
      : null;
  const defaultCallbackUrl = getFreeImageTrialHref();
  const callbackUrl =
    propCallbackUrl ??
    (paramCallbackUrl ? paramCallbackUrl : defaultCallbackUrl);
  const [error, setError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState<string | undefined>(undefined);
  const [isPending, setIsPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const credentialLoginEnabled =
    websiteConfig.auth?.enableCredentialLogin ?? true;
  const googleLoginEnabled = websiteConfig.auth?.enableGoogleLogin ?? false;
  const RegisterSchema = z.object({
    email: z.email({ message: m.auth_register_email_required() }),
    password: z
      .string()
      .min(1, { message: m.auth_register_password_required() }),
    name: z.string().min(1, { message: m.auth_register_name_required() }),
  });
  const form = useForm<z.infer<typeof RegisterSchema>>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: { email: '', password: '', name: '' },
  });
  const onSubmit = async (values: z.infer<typeof RegisterSchema>) => {
    await authClient.signUp.email(
      {
        email: values.email,
        password: values.password,
        name: values.name,
        callbackURL: callbackUrl,
      },
      {
        onRequest: () => {
          setIsPending(true);
          setError('');
          setSuccess('');
        },
        onResponse: () => setIsPending(false),
        onSuccess: () => setSuccess(m.auth_register_check_email()),
        onError: (ctx) => {
          setError(getAuthErrorMessage(ctx.error));
        },
      }
    );
  };
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  return (
    <AuthCard
      headerLabel={m.auth_register_create_account()}
      bottomButtonLabel={m.auth_register_sign_in_hint()}
      bottomButtonHref={Routes.Login}
      bottomButtonSearch={{ callbackUrl }}
      showFooter={showBottomLink}
    >
      <div className="space-y-6">
        <LoginCreditOfferPanel context={creditOffer} />
        <SocialLoginButton callbackUrl={callbackUrl} showDivider={false} />
        {credentialLoginEnabled && googleLoginEnabled && (
          <DividerWithText text={m.auth_social_or()} />
        )}
        {credentialLoginEnabled && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{m.auth_register_name()}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder={m.auth_register_placeholder_name()}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{m.auth_register_email()}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder={m.auth_register_placeholder_email()}
                          type="email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{m.auth_register_password()}</FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            {...field}
                            disabled={isPending}
                            placeholder={m.auth_register_placeholder_password()}
                            type={showPassword ? 'text' : 'password'}
                            className="pr-10"
                          />
                        </FormControl>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 border-0 bg-transparent hover:bg-transparent hover:opacity-70 dark:hover:bg-transparent"
                          onClick={togglePasswordVisibility}
                          disabled={isPending}
                        >
                          {showPassword ? (
                            <IconEyeOff className="size-4 text-muted-foreground" />
                          ) : (
                            <IconEye className="size-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">
                            {showPassword
                              ? m.auth_register_hide_password()
                              : m.auth_register_show_password()}
                          </span>
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormError message={error} />
              <FormSuccess message={success} />
              <Button
                disabled={isPending}
                size="lg"
                type="submit"
                className="w-full flex items-center justify-center gap-2"
              >
                {isPending && <IconLoader2 className="size-4 animate-spin" />}
                <span>{m.auth_register_sign_up()}</span>
              </Button>
            </form>
          </Form>
        )}
      </div>
    </AuthCard>
  );
}
